// Client-side helpers for the HITL handoff token. We peek at its `tenant_id` to
// scope the portal to the case's workspace on the review route, and to render a
// "wrong workspace" hint when a resolve fails. This is display + client-side
// tenant scoping only — never authorization: the backend resolve endpoint and the
// chat WebSocket re-verify the token (Option C), so a forged tenant is rejected
// server-side.

function base64UrlDecode(segment: string): string {
  const pad = segment.length % 4 === 0 ? '' : '='.repeat(4 - (segment.length % 4));
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const binary = atob(b64);
  // `atob` yields one char per byte, so a payload with non-ASCII claims (a
  // username with an umlaut) would mojibake. Re-decode the bytes as UTF-8.
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

// The decoded payload of any JWT, or null if it can't be read. Display and
// client-side bookkeeping only — the signature is NOT verified here, so nothing
// this returns may be used for an authorization decision.
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const data = JSON.parse(base64UrlDecode(payload));
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

// When the token says it expires, or null if it carries no readable `exp`.
// `exp` is seconds since the epoch (RFC 7519 §4.1.4), not milliseconds.
export function decodeJwtExp(token: string): Date | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
  return new Date(exp * 1000);
}

// When this token was minted (`iat`, RFC 7519 4.1.6), or null if absent.
// Seconds since the epoch, like `exp`.
export function decodeJwtIssuedAt(token: string): Date | null {
  const iat = decodeJwtPayload(token)?.iat;
  if (typeof iat !== 'number' || !Number.isFinite(iat)) return null;
  return new Date(iat * 1000);
}

/**
 * When the *session* ends for good, however often its token is renewed.
 *
 * `exp` is when this token dies; `session_exp` is the absolute cap the backend
 * will not extend past, so a renewal offered after it would fail. Same seconds
 * encoding as `exp`.
 *
 * Absent on any token minted before the backend added the claim, which is the
 * normal case until that ships — callers must treat null as "no known cap".
 */
export function decodeJwtSessionEnd(token: string): Date | null {
  const sessionExp = decodeJwtPayload(token)?.session_exp;
  if (typeof sessionExp !== 'number' || !Number.isFinite(sessionExp)) return null;
  return new Date(sessionExp * 1000);
}

// The tenant the handoff token is bound to, or null if the token can't be read.
// Defensive: never throws — a malformed token just yields no hint.
export function decodeHandoffTenant(token: string): string | null {
  const tenantId = decodeJwtPayload(token)?.tenant_id;
  return typeof tenantId === 'string' ? tenantId : null;
}

// First `n` chars of a tenant id (+ ellipsis), so we hint at the target workspace
// without printing the full id of a workspace the viewer may not belong to.
export function shortTenant(tenantId: string, n = 8): string {
  return tenantId.length > n ? `${tenantId.slice(0, n)}…` : tenantId;
}
